import React, { useState, useEffect } from 'react';
import { Table, Typography, Tag, Space, Card, Row, Col, Statistic, Button, message, Popconfirm, Empty } from 'antd';
import {
  AuditOutlined, AppstoreOutlined, UserOutlined, WarningOutlined,
  CheckOutlined, CloseOutlined, DeleteOutlined,
} from '@ant-design/icons';
import api from '../api';

const { Title, Text } = Typography;

function AuditWorkbenchPage() {
  const [data, setData] = useState({ counts: {}, miniPrograms: [], unverifiedUsers: [], flaggedMoments: [] });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get('/admin/audit-workbench/todo');
      setData(d);
    } catch (e) {
      message.error('加载审计工作台失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // 通过小程序审核
  const approveMp = async (item) => {
    try {
      await api.post(`/admin/mini-programs/${item.mini_program_id}/versions/${item.id}/approve`, { reason: '审计通过' });
      message.success('已通过小程序审核');
      load();
    } catch (e) { message.error('操作失败'); }
  };
  const rejectMp = async (item) => {
    try {
      await api.post(`/admin/mini-programs/${item.mini_program_id}/versions/${item.id}/reject`, { reason: '审计驳回' });
      message.success('已驳回小程序');
      load();
    } catch (e) { message.error('操作失败'); }
  };

  // 用户核验 / 禁用
  const verifyUser = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}/verify-email`);
      message.success('已通过邮箱核验');
      load();
    } catch (e) { message.error('操作失败'); }
  };
  const disableUser = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}/disable`);
      message.success('已禁用该用户');
      load();
    } catch (e) { message.error('操作失败'); }
  };

  // 删除命中敏感词内容
  const deleteMoment = async (m) => {
    try {
      await api.delete(`/admin/moments/${m.id}`);
      message.success('已删除该动态');
      load();
    } catch (e) { message.error('操作失败'); }
  };

  const mpColumns = [
    { title: '小程序', dataIndex: 'mp_name', render: (v) => v || '-' },
    { title: '版本', dataIndex: 'version', render: (v) => <Tag>{v || '-'}</Tag> },
    { title: '开发者', dataIndex: 'developer_name', render: (v) => v || '-' },
    { title: '提交时间', dataIndex: 'submit_audit_time', render: (v) => v || '-' },
    {
      title: '操作', width: 160,
      render: (_, r) => (
        <Space>
          <Button type="primary" size="small" icon={<CheckOutlined />} onClick={() => approveMp(r)}>通过</Button>
          <Popconfirm title="确定驳回?" onConfirm={() => rejectMp(r)}>
            <Button danger size="small" icon={<CloseOutlined />}>驳回</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const userColumns = [
    { title: '昵称', dataIndex: 'nickname', render: (v) => v || '-' },
    { title: '手机', dataIndex: 'phone', render: (v) => v || '-' },
    { title: '邮箱', dataIndex: 'email', render: (v) => v || '-' },
    { title: '注册时间', dataIndex: 'created_at', render: (v) => v || '-' },
    {
      title: '操作', width: 160,
      render: (_, r) => (
        <Space>
          <Button type="primary" size="small" icon={<CheckOutlined />} onClick={() => verifyUser(r)}>通过核验</Button>
          <Popconfirm title="确定禁用该用户?" onConfirm={() => disableUser(r)}>
            <Button danger size="small" icon={<CloseOutlined />}>禁用</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const momentColumns = [
    { title: '用户', dataIndex: 'nickname', width: 120, render: (v) => v || '-' },
    {
      title: '内容', ellipsis: true,
      render: (_, r) => (
        <span>
          <Tag color="error">{r.hitWord}</Tag>
          {r.content || '(无文字)'}
        </span>
      ),
    },
    { title: '时间', dataIndex: 'created_at', width: 180, render: (v) => v || '-' },
    {
      title: '操作', width: 100,
      render: (_, r) => (
        <Popconfirm title="确定删除该动态?" onConfirm={() => deleteMoment(r)}>
          <Button danger size="small" icon={<DeleteOutlined />}>删除</Button>
        </Popconfirm>
      ),
    },
  ];

  const Section = ({ title, icon, count, children }) => (
    <Card
      style={{ marginBottom: 16 }}
      title={<Space>{icon}<span>{title}</span><Tag color={count > 0 ? 'red' : 'default'}>{count}</Tag></Space>}
    >
      {count === 0 ? <Empty description="暂无待处理" /> : children}
    </Card>
  );

  return (
    <>
      <Title level={4}>审计工作台</Title>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card><Statistic title="待审小程序" value={data.counts.miniPrograms || 0} prefix={<AppstoreOutlined style={{ color: '#1890ff' }} />} /></Card>
        </Col>
        <Col span={8}>
          <Card><Statistic title="待核验用户" value={data.counts.unverifiedUsers || 0} prefix={<UserOutlined style={{ color: '#fa8c16' }} />} /></Card>
        </Col>
        <Col span={8}>
          <Card><Statistic title="命中敏感词内容" value={data.counts.flaggedMoments || 0} prefix={<WarningOutlined style={{ color: '#ff4d4f' }} />} /></Card>
        </Col>
      </Row>

      <Section title="待审小程序" icon={<AppstoreOutlined />} count={data.counts.miniPrograms || 0}>
        <Table rowKey="id" size="small" pagination={false} columns={mpColumns} dataSource={data.miniPrograms} scroll={{ x: 700 }} />
      </Section>

      <Section title="待核验用户（邮箱未验证）" icon={<UserOutlined />} count={data.counts.unverifiedUsers || 0}>
        <Table rowKey="id" size="small" pagination={false} columns={userColumns} dataSource={data.unverifiedUsers} scroll={{ x: 700 }} />
      </Section>

      <Section title="命中敏感词的朋友圈内容" icon={<WarningOutlined />} count={data.counts.flaggedMoments || 0}>
        <Table rowKey="id" size="small" pagination={false} columns={momentColumns} dataSource={data.flaggedMoments} scroll={{ x: 700 }} />
      </Section>
    </>
  );
}

export default AuditWorkbenchPage;
